// Shared renderer for CMS block content. Used by the public article route and
// by the admin live preview so what writers see is exactly what ships.
import { Link } from "@tanstack/react-router";
import { ArrowRight, Quote as QuoteIcon, ExternalLink, Lightbulb } from "lucide-react";
import { PRODUCT_TITLES, type Block } from "@/lib/blog/blocks";
import { imageSrcSet, sizedImageUrl } from "@/lib/image-url";


function Paragraph({ text }: { text: string }) {
  return (
    <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {text}
    </p>
  );
}

export function BlockRenderer({ blocks }: { blocks: Block[] }) {
  return (
    <div className="mt-8">
      {blocks.map((b) => {
        switch (b.type) {
          case "heading":
            return b.level === 3 ? (
              <h3
                key={b.id}
                className="mt-8 font-display text-xl font-bold text-brand-deep md:text-2xl"
              >
                {b.text}
              </h3>
            ) : (
              <h2
                key={b.id}
                className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl"
              >
                {b.text}
              </h2>
            );

          case "paragraph":
            return <Paragraph key={b.id} text={b.text} />;

          case "bullets":
            return (
              <ul key={b.id} className="mt-4 space-y-2">
                {b.items.filter(Boolean).map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-2.5 text-base leading-relaxed text-muted-foreground"
                  >
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                    <span className="min-w-0 break-words">{item}</span>
                  </li>
                ))}
              </ul>
            );

          case "numbers":
            return (
              <ol key={b.id} className="mt-4 space-y-2">
                {b.items.filter(Boolean).map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-base leading-relaxed text-muted-foreground"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-bold text-brand">
                      {i + 1}
                    </span>
                    <span className="min-w-0 break-words">{item}</span>
                  </li>
                ))}
              </ol>
            );

          case "callout":
            return (
              <aside
                key={b.id}
                className="mt-8 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 md:p-6"
              >
                <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
                  <Lightbulb className="h-3.5 w-3.5" />
                  {b.title || "Key takeaway"}
                </p>
                <p className="mt-2 text-base leading-relaxed text-brand-deep">{b.text}</p>
              </aside>
            );

          case "quote":
            return (
              <blockquote
                key={b.id}
                className="mt-8 rounded-2xl border-l-4 border-brand bg-card p-5 md:p-6"
              >
                <QuoteIcon className="h-4 w-4 text-brand" />
                <p className="mt-2 text-base italic leading-relaxed text-brand-deep md:text-lg">
                  {b.text}
                </p>
                {b.attribution ? (
                  <footer className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {b.attribution}
                  </footer>
                ) : null}
              </blockquote>
            );

          case "table":
            return (
              <div key={b.id} className="mt-8 overflow-x-auto rounded-2xl border border-border">
                <table className="w-full min-w-[420px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-card">
                      {b.headers.map((h, i) => (
                        <th
                          key={i}
                          className="border-b border-border px-4 py-3 text-left font-semibold text-brand-deep"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, ri) => (
                      <tr key={ri} className="odd:bg-background even:bg-card/50">
                        {r.map((c, ci) => (
                          <td
                            key={ci}
                            className="border-b border-border px-4 py-3 align-top text-muted-foreground"
                          >
                            {c}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case "image":
            if (!b.url) return null;
            return (
              <figure key={b.id} className="mt-8">
                <img
                  src={sizedImageUrl(b.url, 960)}
                  srcSet={imageSrcSet(b.url)}
                  sizes="(min-width: 768px) 720px, 100vw"
                  alt={b.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full rounded-2xl border border-border object-cover"
                />

                {b.caption ? (
                  <figcaption className="mt-2 text-xs text-muted-foreground">
                    {b.caption}
                  </figcaption>
                ) : null}
              </figure>
            );

          case "faq":
            return (
              <div key={b.id} className="mt-6 rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                  {b.question}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.answer}</p>
              </div>
            );

          case "product":
            return (
              <Link
                key={b.id}
                to="/products/$handle"
                params={{ handle: b.handle }}
                className="group mt-8 flex flex-col gap-2 rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-luxury"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
                  From the TruWe range
                </p>
                <p className="font-display text-xl font-bold text-brand-deep group-hover:text-brand">
                  {PRODUCT_TITLES[b.handle]}
                </p>
                <p className="text-sm text-muted-foreground">{b.blurb}</p>
                <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-brand">
                  View product <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            );

          case "reference":
            return (
              <p key={b.id} className="mt-3 text-xs text-muted-foreground">
                <a
                  href={b.url}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="inline-flex items-center gap-1 underline hover:text-brand"
                >
                  {b.label || b.url}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

export default BlockRenderer;
